import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const values = [
    { icon: 'ri-shield-star-line', title: 'Excellence', description: 'Committed to the highest standards in every aspect of service' },
    { icon: 'ri-heart-line', title: 'Integrity', description: 'Building trust through transparency and honest communication' },
    { icon: 'ri-lightbulb-line', title: 'Innovation', description: 'Continuously improving to exceed client expectations' },
    { icon: 'ri-team-line', title: 'Partnership', description: 'Creating lasting relationships with our valued clients' },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about`,
      name: 'About YESJETS',
      url: `${siteUrl}/about`,
      description: 'Founded in 2009, YESJETS has grown from a boutique charter operation to one of the most respected names in private aviation.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'YESJETS',
      url: siteUrl,
      foundingDate: '2009',
      description: 'YESJETS is a premier Chicago-based luxury private jet charter company offering on-demand aircraft rental, 24/7 concierge service, and access to a global network of over 5,000 airports.',
      telephone: '+17085678866',
      email: 'Flights@flyyesjets.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Chicago',
        addressRegion: 'IL',
        addressCountry: 'US',
      },
      numberOfEmployees: { '@type': 'QuantitativeValue', value: 50 },
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="About YESJETS | Chicago Private Jet Charter Company Since 2009"
        description="Learn about YESJETS — Chicago's premier private jet charter company. 15+ years of luxury aviation, 50+ aircraft, 98% client satisfaction, and 24/7 concierge service worldwide."
        keywords="about YESJETS, Chicago private jet company, luxury aviation history, private charter operator, YESJETS story"
        canonical="/about"
        jsonLd={jsonLd}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[70vh] w-full overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b5d126816f4793199c7c02ab99f1deaf.jpg"
            alt="YESJETS Facility"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        <div className="relative h-full flex items-center justify-center px-6 md:px-8">
          <div className="text-center text-white max-w-4xl">
            <div className="inline-block px-4 md:px-6 py-2 bg-gold/20 border border-gold rounded-full text-gold text-xs md:text-sm font-sans tracking-wider mb-4 md:mb-6">
              OUR STORY
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif font-semibold mb-4 md:mb-6">
              ABOUT YESJETS
            </h1>
            <p className="text-base md:text-2xl font-sans text-gray-200 leading-relaxed">
              Redefining luxury private aviation
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-16 items-start">
            <div className="md:col-span-3">
              <h2 className="text-3xl md:text-5xl font-serif font-semibold text-black mb-6 md:mb-8 leading-tight">
                Elevating Private Aviation to New Heights
              </h2>
              <div className="space-y-6 text-gray-700 font-sans text-base md:text-lg leading-relaxed">
                <p>
                  At YESJETS, we believe private aviation should feel effortless, responsive, and tailored to the way you actually travel. Clients choose our charter services because we combine speed with judgment — not just access to aircraft, but thoughtful guidance on the best option for your route, schedule, passenger count, and overall experience. Whether you are flying for business, leisure, or a last-minute need, we focus on finding the right solution quickly, clearly, and without unnecessary friction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Brand Values Section */}
      <section className="py-20 md:py-28 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20luxury%20gold%20geometric%20pattern%20with%20fine%20lines%20and%20subtle%20texture%20on%20deep%20dark%20navy%20background%20elegant%20minimal%20aviation%20inspired%20motif%20for%20premium%20brand%20identity&width=1920&height=800&seq=mission-bg-001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 md:px-8 text-center">
          <div className="mb-14 md:mb-20">
            <div className="inline-block px-4 md:px-6 py-2 bg-gold/20 border border-gold rounded-full text-gold text-xs md:text-sm font-sans tracking-widest mb-6 md:mb-8">
              OUR MISSION
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-semibold text-white leading-tight mb-6 md:mb-8 max-w-3xl mx-auto">
              To Deliver the World's Most Seamless Private Aviation Experience
            </h2>
          </div>

          <div className="flex items-center justify-center gap-4 mb-14 md:mb-20">
            <div className="h-px w-16 md:w-24 bg-gold/40"></div>
            <i className="ri-plane-line text-gold text-xl"></i>
            <div className="h-px w-16 md:w-24 bg-gold/40"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
            {[
              { icon: 'ri-compass-3-line', title: 'Freedom to Fly', description: 'We remove every barrier between you and your destination. No queues, no compromises — just pure, uninterrupted travel freedom.' },
              { icon: 'ri-award-line', title: 'Unmatched Standards', description: 'Every aircraft, every crew member, every detail is held to the highest benchmark in private aviation — because you deserve nothing less.' },
              { icon: 'ri-user-heart-line', title: 'Personal at Every Step', description: 'From your first call to your final landing, our team is dedicated to crafting a journey that feels uniquely and personally yours.' },
            ].map((pillar, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 hover:bg-white/10 transition-all duration-300">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gold/15 border border-gold/40 mx-auto mb-5 md:mb-6">
                  <i className={`${pillar.icon} text-2xl text-gold`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-serif font-semibold text-white mb-3 md:mb-4">{pillar.title}</h3>
                <p className="text-gray-400 font-sans text-sm leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#f8f7f5]">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-serif font-semibold text-black mb-4">Our Core Values</h2>
            <p className="text-base md:text-lg text-gray-600 font-sans">The principles that guide everything we do</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold/10 border-2 border-gold flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <i className={`${value.icon} text-3xl md:text-4xl text-gold`}></i>
                </div>
                <h3 className="text-lg md:text-xl font-serif font-semibold text-black mb-2 md:mb-3">{value.title}</h3>
                <p className="text-gray-600 font-sans text-xs md:text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-charcoal">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-serif font-light text-white mb-5 md:mb-6">
            Ready to Experience YESJETS?
          </h2>
          <p className="text-base md:text-xl text-gray-400 font-sans mb-8 md:mb-10">
            Let us show you what sets us apart in private aviation
          </p>
          <a
            href="/booking"
            className="inline-flex items-center gap-3 bg-gold text-black px-8 md:px-10 py-3 md:py-4 rounded-full text-sm md:text-base font-sans font-semibold tracking-wide hover:bg-gold/90 transition-all cursor-pointer whitespace-nowrap"
          >
            BOOK YOUR FLIGHT
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
